import { computed, onBeforeUnmount, ref, watch, type Ref } from 'vue'
import { askAnalyst, type AnalystAnswer, type AnalystEvent, type ScreenContext } from '@/api/analyst'
import { isApiError } from '@/api/client'
import { stageOf, type Stage } from '@/lib/analyst'

/** One question and what came of it. */
export interface Turn {
  key: number
  question: string
  /** Where the engine is, while it works; null once it has answered or failed. */
  stage: Stage | null
  attempt: number
  metricIds: string[]
  /** The days being computed, once the engine has resolved them. */
  period: { first: string; last: string } | null
  answer: AnalystAnswer | null
  error: { code: string; message: string } | null
}

/**
 * One conversation with the Analyst in one workspace. The server keeps it
 * (the engine reads the last turns, so "and the week before?" works); the
 * panel keeps its turns while the page is open. A new conversation, or
 * another workspace, starts again, and stops any answer still arriving.
 */
export const useAnalystConversation = (workspaceId: Ref<string | undefined>) => {
  const turns = ref<Turn[]>([])
  const conversationId = ref<string | undefined>()
  const busy = computed(() => turns.value.some((turn) => turn.stage !== null))
  let controller: AbortController | null = null
  let nextKey = 0

  const stop = (): void => {
    controller?.abort()
    controller = null
  }

  const reset = (): void => {
    stop()
    turns.value = []
    conversationId.value = undefined
  }

  const ask = async (question: string, context: ScreenContext): Promise<void> => {
    const text = question.trim()
    const workspace = workspaceId.value
    if (!text || !workspace || busy.value) return

    const length = turns.value.push({ key: nextKey++, question: text, stage: 'READING', attempt: 1, metricIds: [], period: null, answer: null, error: null })
    // Through the reactive array, so each change below redraws the turn.
    const turn = turns.value[length - 1]!
    const mine = new AbortController()
    controller = mine

    const onEvent = (event: AnalystEvent): void => {
      if (event.type === 'step') {
        if (event.status !== 'STARTED') return
        turn.stage = stageOf(event.step)
        turn.attempt = event.attempt
        if (event.metricIds.length > 0) turn.metricIds = event.metricIds
        if (event.period) turn.period = event.period
      } else if (event.type === 'answer') {
        turn.answer = event.answer
        turn.stage = null
        conversationId.value = event.answer.conversationId
      } else {
        turn.error = { code: event.code, message: event.message }
        turn.stage = null
      }
    }

    try {
      await askAnalyst(workspace, { question: text, conversationId: conversationId.value, context }, onEvent, mine.signal)
      if (mine.signal.aborted) return
      if (turn.stage !== null) {
        turn.error = { code: 'STREAM_ENDED', message: '' }
        turn.stage = null
      }
    } catch (error) {
      turn.error = isApiError(error) ? { code: error.code, message: error.message } : { code: 'NETWORK_ERROR', message: '' }
      turn.stage = null
    } finally {
      if (controller === mine) controller = null
    }
  }

  watch(workspaceId, reset)
  onBeforeUnmount(stop)

  return { turns, busy, ask, reset }
}
